import { compact, isEmpty } from 'lodash';
import { useCallback } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  ExtendedTypeaheadQueryOpts,
  useAsyncTypeaheadProps,
} from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { FormAwareTypeaheadMultiSelectInputProps } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';

import {
  GetTeamMembersMultipleQuery,
  GetTeamMembersMultipleQueryVariables,
  useGetTeamMembersMultipleQuery,
} from '../graphql/TeamMembersTypeahead.generated';

type TeamMembersTypeaheadProps<FormShape extends FieldValues> = Omit<
  FormAwareTypeaheadMultiSelectInputProps<FormShape>,
  'options' | 'inputValue' | 'onInputChange'
>;

export function useTeamMembersTypeahead<FormShape extends FieldValues>(
  fieldName: TeamMembersTypeaheadProps<FormShape>['fieldName']
) {
  const { createErrorFeedback } = useFeedback();
  const { control } = useFormContext<FormShape>();

  // Watch currently-selected ids so we can ask the backend for them
  const selectedIds = useWatch({ control, name: fieldName }) as string[];

  // Map the user's search text => GraphQL variables for queryData
  const searchTermToVariables = useCallback(
    (search: string) => ({
      query: search,
    }),
    []
  );

  // Always ask for the full records of already-selected items
  const getSelectedOptionVariables = useCallback(
    () => ({
      selectedOptionsLike: {
        idIn: isEmpty(selectedIds) ? [''] : compact(selectedIds),
      },
    }),
    [selectedIds]
  );

  const toOptions: ExtendedTypeaheadQueryOpts<
    string,
    GetTeamMembersMultipleQuery,
    GetTeamMembersMultipleQueryVariables
  >['toOptions'] = useCallback(
    (rows) =>
      rows.map((employee) => ({
        value: employee.id,
        display: employee.displayName,
      })),
    []
  );

  const [typeaheadProps, queryResult] = useAsyncTypeaheadProps(
    useGetTeamMembersMultipleQuery,
    {
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      pageSize: 50,
      onError: createErrorFeedback(
        'Failed to fetch employees. Please refresh the page to try again.'
      ),
      unselectedGroupLabel: 'Available team members',
    }
  );

  return { typeaheadProps, queryResult };
}
